import { Box, Checkbox, CircularProgress, FormControlLabel, Tooltip, Typography } from "@mui/material";
import { DataGrid, type GridColDef, type GridRenderCellParams } from "@mui/x-data-grid";
import { DateTime } from "luxon";
import { type FC, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import ColorfulRole from "../../../components/UIToolkit/ColorfulRole/ColorfulRole";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { FEATURE_FLAGS } from "../../../contexts/FeatureFlagsProvider";
import { useDepartmentFlags } from "../../../hooks/useDepartmentFlags";
import { useFormatPersonName } from "../../../hooks/useNameFormat";
import { useFeatureFlag } from "../../../hooks/useOpenFeatureFlag";
import type { ShiftTimeRange, StaffMemberDailyBreakdown } from "../../../utils/models";
import { formatCurrency } from "../../../utils/utilFunctions";
import { MC_EXPANDED_ROW_BG, MC_GRID_SX } from "../utils";
import { useGetDailyStaffBreakdown } from "./hooks";
import { ScheduleVsActualProblemsView } from "./ScheduleVsActualProblemsView";
import type { Metric } from "./types";
import { getDifferenceColor } from "./utils";

type Props = {
	date: string;
	metric: Metric;
	roles?: string[];
	jobs?: string[];
	laborCost?: boolean;
	shiftHours?: string[];
	includeLateScheduled?: boolean;
	estimateMissingWage: boolean;
};

const formatShiftTimes = (shifts: ShiftTimeRange[], timezone: string): string => {
	if (!shifts || shifts.length === 0) return "";
	return shifts
		.map((s) => {
			const start = DateTime.fromISO(s.startTime).setZone(timezone).toFormat("h:mm a");
			const end = DateTime.fromISO(s.endTime).setZone(timezone).toFormat("h:mm a");
			return `${start} - ${end}`;
		})
		.join("\n");
};

const HoursCell: FC<{ staff: StaffMemberDailyBreakdown; type: "scheduled" | "actual"; isPpd: boolean; timezone: string }> = ({
	staff,
	type,
	isPpd,
	timezone,
}) => {
	const value = type === "scheduled" ? (isPpd ? staff.scheduledCost : staff.scheduledHours) : isPpd ? staff.actualCost : staff.actualHours;
	const shifts = type === "scheduled" ? staff.scheduledShifts : staff.actualShifts;
	const tooltipText = formatShiftTimes(shifts, timezone);
	const formattedValue = isPpd ? formatCurrency(value) : value.toFixed(2);

	if (tooltipText) {
		return (
			<Tooltip title={<span style={{ whiteSpace: "pre-line" }}>{tooltipText}</span>} arrow placement="top">
				<Typography variant="body2" sx={{ cursor: "help", textDecoration: "underline dotted" }}>
					{formattedValue}
				</Typography>
			</Tooltip>
		);
	}
	return <Typography variant="body2">{formattedValue}</Typography>;
};

export const ScheduleVsActualExpandedRow: FC<Props> = ({ date, metric, roles, jobs, shiftHours, laborCost, includeLateScheduled, estimateMissingWage }) => {
	const { t } = useTranslation();
	const { timezone } = useDepartmentFlags();
	const formatPersonName = useFormatPersonName();
	const { track } = useAnalytics();
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);
	const [showDetails, setShowDetails] = useState(false);
	const { data, isLoading } = useGetDailyStaffBreakdown({
		date,
		enabled: true,
		roles,
		jobs,
		shiftHours,
		laborCost,
		includeProblems: true,
		includeLateScheduled,
		estimateMissingWage,
	});

	const isPpd = metric === "ppd";

	const handleToggle = useCallback(
		(_e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
			setShowDetails(checked);
			track("schedule_vs_actual_view_toggled", { date, view: checked ? "details" : "problems" });
		},
		[date, track],
	);

	const detailColumns: GridColDef<StaffMemberDailyBreakdown>[] = useMemo(
		() => [
			{
				field: "role",
				// INH-3407: show Job display name in place of Role when job_management_enabled is on
				// and the row carries a job assignment (fallback to role keeps non-job rows readable).
				headerName: jobManagementEnabled ? t("job") : t("schedule.vs.actual.role"),
				flex: 0.6,
				display: "flex",
				renderCell: (params: GridRenderCellParams<StaffMemberDailyBreakdown>) => {
					if (jobManagementEnabled && params.row.jobDisplayName) {
						return <Typography variant="body2">{params.row.jobDisplayName}</Typography>;
					}
					return <ColorfulRole role={params.row.role} />;
				},
			},
			{
				field: "name",
				headerName: t("schedule.vs.actual.name"),
				flex: 1.5,
				valueGetter: (_value: unknown, row: StaffMemberDailyBreakdown) => formatPersonName(row),
				sortComparator: (v1: string, v2: string) => v1.localeCompare(v2),
			},
			{
				field: "scheduledHours",
				headerName: t("schedule.vs.actual.scheduled"),
				flex: 1,
				align: "center",
				headerAlign: "center",
				type: "number",
				display: "flex",
				valueGetter: (_value: unknown, row: StaffMemberDailyBreakdown) => (isPpd ? row.scheduledCost : row.scheduledHours),
				renderCell: (params: GridRenderCellParams<StaffMemberDailyBreakdown>) => (
					<HoursCell staff={params.row} type="scheduled" isPpd={isPpd} timezone={timezone} />
				),
			},
			{
				field: "actualHours",
				headerName: t("schedule.vs.actual.actual"),
				flex: 1,
				align: "center",
				headerAlign: "center",
				type: "number",
				display: "flex",
				valueGetter: (_value: unknown, row: StaffMemberDailyBreakdown) => (isPpd ? row.actualCost : row.actualHours),
				renderCell: (params: GridRenderCellParams<StaffMemberDailyBreakdown>) => (
					<HoursCell staff={params.row} type="actual" isPpd={isPpd} timezone={timezone} />
				),
			},
			{
				field: "difference",
				headerName: t("schedule.vs.actual.difference"),
				flex: 1,
				align: "center",
				headerAlign: "center",
				type: "number",
				display: "flex",
				valueGetter: (_value: unknown, row: StaffMemberDailyBreakdown) => {
					const scheduled = isPpd ? row.scheduledCost : row.scheduledHours;
					const actual = isPpd ? row.actualCost : row.actualHours;
					return scheduled !== 0 ? Math.abs(actual - scheduled) / Math.abs(scheduled) : 0;
				},
				renderCell: (params: GridRenderCellParams<StaffMemberDailyBreakdown>) => {
					const scheduled = isPpd ? params.row.scheduledCost : params.row.scheduledHours;
					const actual = isPpd ? params.row.actualCost : params.row.actualHours;
					const diff = actual - scheduled;
					const color = getDifferenceColor(scheduled, actual);
					return (
						<Typography
							variant="body2"
							sx={{
								color,
								fontWeight: diff !== 0 ? 600 : 400,
								lineHeight: "inherit",
							}}
						>
							{isPpd ? formatCurrency(diff) : diff.toFixed(2)}
						</Typography>
					);
				},
			},
		],
		[t, isPpd, timezone, jobManagementEnabled, formatPersonName],
	);

	if (isLoading) {
		return (
			<Box
				sx={{
					display: "flex",
					justifyContent: "center",
					py: 2,
				}}
			>
				<CircularProgress size={24} />
			</Box>
		);
	}

	if (!data?.staff || data.staff.length === 0) {
		return (
			<Box
				sx={{
					py: 2,
					px: 4,
				}}
			>
				<Typography
					variant="body2"
					sx={{
						color: "text.secondary",
					}}
				>
					{t("schedule.vs.actual.no.data")}
				</Typography>
			</Box>
		);
	}

	return (
		<Box sx={{ bgcolor: MC_EXPANDED_ROW_BG, py: 1, px: 4, width: "100%", boxSizing: "border-box" }}>
			<Box
				sx={{
					display: "flex",
					justifyContent: "flex-end",
					mb: 0.5,
				}}
			>
				<FormControlLabel
					control={<Checkbox size="small" checked={showDetails} onChange={handleToggle} />}
					label={<Typography variant="body2">{t("schedule.vs.actual.show.details")}</Typography>}
				/>
			</Box>
			{showDetails ? (
				<DataGrid
					rows={data.staff}
					columns={detailColumns}
					getRowId={(row) => row.nurseId}
					initialState={{
						sorting: {
							sortModel: [{ field: "difference", sort: "desc" }],
						},
					}}
					density="compact"
					disableColumnMenu
					disableColumnFilter
					hideFooter
					rowSelection={false}
					sx={{
						...MC_GRID_SX,
						bgcolor: "transparent",
					}}
				/>
			) : (
				<ScheduleVsActualProblemsView staff={data.staff} />
			)}
		</Box>
	);
};
